import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Loader2, Award, Clock, BookOpen, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * Learning Hub — landing page that lists every available module with the
 * member's current progress. Tap a card → /platform/learning/:moduleId.
 *
 * Each card shows: cover emoji, title, level, hour estimate, lesson count,
 * progress bar. Completed modules display an Award badge and link to download
 * the certificate PDF.
 */

const LEVEL_BADGE = {
  beginner: "bg-emerald-100 text-emerald-700 border-emerald-300",
  intermediate: "bg-blue-100 text-blue-700 border-blue-300",
  advanced: "bg-amber-100 text-amber-700 border-amber-300",
};

export default function PlatformLearning() {
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/learning/modules");
        setModules(r.data.modules || []);
      } catch (e) {
        toast.error("Could not load modules");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="bg-pearl min-h-screen">
      <div className="px-4 sm:px-8 py-10 max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="eyebrow text-saffron">Dhamma learning</p>
            <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2">Structured study modules.</h1>
          </div>
          <Link
            to="/platform"
            className="text-sm text-navy/60 hover:text-navy"
          >
            ← Sangha home
          </Link>
        </div>
        <p className="text-slate-700 max-w-3xl leading-relaxed">
          Short, self-paced modules. 4-10 lessons each. Read at your own pace; reflect at the end of every lesson;
          download a certificate when you complete a module.
        </p>

        {loading && (
          <div className="text-center py-16 text-slate-500">
            <Loader2 size={20} className="animate-spin mx-auto" />
          </div>
        )}

        {!loading && modules && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="learning-modules-grid">
            {modules.map((m) => (
              <ModuleCard key={m.id} module={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ModuleCard({ module }) {
  const pct = module.progress?.percent || 0;
  const isComplete = module.progress?.is_complete;
  return (
    <Link
      to={`/platform/learning/${module.id}`}
      data-testid={`module-card-${module.id}`}
      className="group bg-white border border-navy/10 rounded-sm p-6 hover:border-saffron/50 hover:shadow-md transition-all relative overflow-hidden"
    >
      {isComplete && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 bg-saffron/15 border border-saffron/40 rounded-sm text-saffron text-[10px] uppercase tracking-widest">
          <Award size={10} /> Completed
        </div>
      )}
      <div className="text-4xl mb-3" aria-hidden>{module.cover_emoji}</div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-[10px] px-2 py-0.5 rounded-sm border uppercase tracking-widest ${LEVEL_BADGE[module.level] || LEVEL_BADGE.beginner}`}>
          {module.level}
        </span>
        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
          <Clock size={10} /> ~{module.estimated_hours} hours
        </span>
        <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
          <BookOpen size={10} /> {module.lesson_count} lessons
        </span>
      </div>
      <h3 className="font-display text-2xl text-navy leading-tight group-hover:text-saffron transition-colors">{module.title}</h3>
      <p className="mt-1 text-sm text-slate-600 italic">{module.subtitle}</p>
      <p className="mt-3 text-[13px] text-slate-700 leading-relaxed line-clamp-3">{module.intro}</p>
      <div className="mt-5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            {module.progress.completed} of {module.progress.total} lessons
          </span>
          <span className="text-[10px] uppercase tracking-widest text-saffron">{pct}%</span>
        </div>
        <div className="h-1.5 bg-navy/10 rounded-sm overflow-hidden">
          <div className="h-full bg-saffron transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-saffron font-medium group-hover:gap-2 transition-all">
        {pct === 0 ? "Start module" : isComplete ? "Review" : "Continue"} <ArrowRight size={14} />
      </div>
    </Link>
  );
}
